import { useCallback } from 'react';

import { ClickAwayListener, Box as Flex } from '@material-ui/core';

import {
  RiCheckboxBlankLine,
  RiFontSize,
  RiImageLine,
  RiLayout2Line,
  RiLayout4Line,
  RiLayoutColumnLine,
  RiPriceTag3Line,
  RiSeparator,
  RiShapesLine,
  RiTable2,
  RiVerifiedBadgeFill,
} from '@remixicon/react';
import { useEditorState } from '@tiptap/react';

import { useCurrentEditor } from '../Context';
import { FloatingCard, ToolbarCard } from '../FloatingCard';
import { ActionItem } from './ActionItem';
import { ToolbarButton } from './ToolbarButton';
import { ToolbarDropdown } from './ToolbarDropdown';
import { ICON_SIZE } from './constants';
import { textFormattingOptions } from './menuOptions';
import { Menu, MenuDivider, MenuItem, MenuItemIcon, useStyles } from './styles';
import { useMenuState } from './useMenuState';

export const FloatingBlockToolbar = () => {
  const classes = useStyles();
  const { editor } = useCurrentEditor();
  const { openMenu, toggleMenu, closeAllMenus } = useMenuState();

  const currentNodeType = useEditorState({
    editor,
    selector: ctx => {
      if (!ctx.editor) return 'paragraph';
      if (ctx.editor.isActive('blockquote')) return 'blockquote';
      if (ctx.editor.isActive('codeBlock')) return 'code-block';
      if (ctx.editor.isActive('bulletList')) return 'bullet-list';
      if (ctx.editor.isActive('orderedList')) return 'numbered-list';
      for (let level = 1; level <= 4; level++) {
        if (ctx.editor.isActive('heading', { level })) return `h${level}`;
      }
      return 'paragraph';
    },
  });

  // --- Editor commands ---

  const handleNodeTypeChange = useCallback(
    (value: string) => {
      if (!editor) return;
      if (value === 'paragraph') {
        editor.chain().focus().setParagraph().run();
      } else if (value === 'blockquote') {
        editor.chain().focus().toggleBlockquote().run();
      } else if (value === 'bullet-list') {
        editor.chain().focus().toggleBulletList().run();
      } else if (value === 'numbered-list') {
        editor.chain().focus().toggleOrderedList().run();
      } else if (value === 'code-block') {
        editor.chain().focus().insertContent({ type: 'codeBlock' }).run();
      } else if (value.startsWith('h')) {
        const level = parseInt(value.substring(1), 10) as 1 | 2 | 3 | 4;
        editor.chain().focus().setHeading({ level }).run();
      }
      closeAllMenus();
    },
    [editor, closeAllMenus],
  );

  const insertColumns = useCallback(
    (count: 2 | 3) => {
      if (!editor) return;
      const columns = Array.from({ length: count }, () => ({
        type: 'column',
        content: [{ type: 'paragraph' }],
      }));
      editor
        .chain()
        .focus()
        .insertContent({ type: 'columns', content: columns })
        .run();
      closeAllMenus();
    },
    [editor, closeAllMenus],
  );

  const insertTable = useCallback(() => {
    editor
      ?.chain()
      .focus()
      .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
      .run();
  }, [editor]);

  const insertImage = useCallback(() => {
    editor?.chain().focus().setImage({ src: '' }).run();
  }, [editor]);

  const insertBlock = useCallback(() => {
    editor?.chain().focus().toggleCalloutBlock().run();
  }, [editor]);

  const insertImageCard = useCallback(() => {
    editor?.chain().focus().setImageCard().run();
  }, [editor]);

  const insertDivider = useCallback(() => {
    editor?.chain().focus().setHorizontalRule().run();
    closeAllMenus();
  }, [editor, closeAllMenus]);

  const insertTag = useCallback(() => {
    editor?.chain().focus().setTag().run();
    closeAllMenus();
  }, [editor, closeAllMenus]);

  const insertHighlight = useCallback(() => {
    editor?.chain().focus().setHighlight().run();
    closeAllMenus();
  }, [editor, closeAllMenus]);

  if (!editor || !editor.isEditable) return null;

  // --- Render helpers ---

  return (
    <ClickAwayListener onClickAway={closeAllMenus}>
      <div className={classes.wrapper} data-testid="floating-block-toolbar">
        <ToolbarCard className={classes.toolbar}>
          <Flex
            display="flex"
            alignItems="center"
            style={{
              gap: 'var(--bui-space-0_5)',
            }}
          >
            <ToolbarDropdown
              id="text"
              label="Text formatting"
              icon={<RiFontSize size={ICON_SIZE} />}
              openMenu={openMenu}
              onToggle={toggleMenu}
            >
              <FloatingCard>
                <Menu role="menu" aria-label="Text formatting options">
                  {textFormattingOptions.map((opt, index) =>
                    opt.type === 'divider' ? (
                      <MenuDivider key={`divider-${index}`} role="separator" />
                    ) : (
                      <MenuItem
                        key={opt.value}
                        role="menuitem"
                        tabIndex={0}
                        className={
                          currentNodeType === opt.value ? 'selected' : ''
                        }
                        onClick={() => handleNodeTypeChange(opt.value)}
                        onKeyDown={e =>
                          e.key === 'Enter' && handleNodeTypeChange(opt.value)
                        }
                      >
                        <MenuItemIcon>{opt.icon}</MenuItemIcon>
                        {opt.label}
                      </MenuItem>
                    ),
                  )}
                </Menu>
              </FloatingCard>
            </ToolbarDropdown>

            <ToolbarButton
              label="Table"
              icon={<RiTable2 size={ICON_SIZE} />}
              onClick={insertTable}
            />
            <ToolbarButton
              label="Image"
              icon={<RiImageLine size={ICON_SIZE} />}
              onClick={insertImage}
            />

            <ToolbarDropdown
              id="columns"
              label="Columns"
              icon={<RiLayoutColumnLine size={ICON_SIZE} />}
              openMenu={openMenu}
              onToggle={toggleMenu}
            >
              <FloatingCard>
                <Menu role="menu" aria-label="Column options">
                  <ActionItem
                    icon={<RiLayoutColumnLine size={ICON_SIZE} />}
                    onClick={() => insertColumns(2)}
                    label="2 columns"
                  />
                  <ActionItem
                    icon={<RiLayout2Line size={ICON_SIZE} />}
                    onClick={() => insertColumns(3)}
                    label="3 columns"
                  />
                </Menu>
              </FloatingCard>
            </ToolbarDropdown>

            <ToolbarButton
              label="Block"
              icon={<RiCheckboxBlankLine size={ICON_SIZE} />}
              onClick={insertBlock}
            />
            <ToolbarButton
              label="Image card"
              icon={<RiLayout4Line size={ICON_SIZE} />}
              onClick={insertImageCard}
            />

            <ToolbarDropdown
              id="other"
              label="Other blocks"
              icon={<RiShapesLine size={ICON_SIZE} />}
              openMenu={openMenu}
              onToggle={toggleMenu}
            >
              <FloatingCard>
                <Menu role="menu" aria-label="Other blocks">
                  <ActionItem
                    icon={<RiSeparator size={ICON_SIZE} />}
                    onClick={insertDivider}
                    label="Divider"
                  />
                  <ActionItem
                    icon={<RiPriceTag3Line size={ICON_SIZE} />}
                    onClick={insertTag}
                    label="Tag"
                  />
                  <ActionItem
                    icon={<RiVerifiedBadgeFill size={ICON_SIZE} />}
                    onClick={insertHighlight}
                    label="Highlight"
                  />
                </Menu>
              </FloatingCard>
            </ToolbarDropdown>
          </Flex>
        </ToolbarCard>
      </div>
    </ClickAwayListener>
  );
};
